import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import flowBackEndService from "../../services/flowBackendServices";
import { useDispatch } from "react-redux";
import moment from "moment";
import { SESSION_TIME, STORAGE_KEY } from "../../utils/config";
import {
  setUser,
  setSessionTime,
  setOtpData,
  setEmailAndMobile,
  loginEvent
} from "../../slices/authReducer";
import {
  type LoginInfo,
  type SsoLoginRequest,
  type SsoLoginApiResponse,
  type ApiUserResponse,
  normalizeUserData,
  isValidApiUserResponse,
  isValidLoginInfo,
  validateUserData,
  getClientCode,
  AUTH_STATUS
} from "../../models/login";
import { window } from "rxjs/operators";

/**
 * SSO Authentication component that handles Single Sign-On login flow
 * with comprehensive type safety and error handling
 */
const AuthStoreWithSSO: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authProcessed, setAuthProcessed] = useState<boolean>(false);

  useEffect(() => {
    if (authProcessed) return; // Prevent multiple executions

    const handleAuthentication = async (): Promise<void> => {
      try {
        setIsLoading(true);

        const ssoAuthToken = searchParams.get('sso_auth_token');
        const path = searchParams.get('path');
        const clientCode = searchParams.get('client_code');
        const version = searchParams.get('version');
        const symbol = searchParams.get('symbol');
        if (symbol) {
          sessionStorage.setItem('new_symbol', symbol);
        }

        if (ssoAuthToken) {
          await authenticateWithSSO(ssoAuthToken, path, clientCode, version);
        } else {
          await checkExistingAuth();
        }
      } catch (error) {
        console.error('Authentication flow error:', error);
        handleAuthError('Authentication failed');
      } finally {
        setIsLoading(false);
        setAuthProcessed(true);
      }
    };

    handleAuthentication();
  }, [searchParams, navigate, dispatch, authProcessed]);

  /**
   * Check if user has existing valid authentication
   */
  const checkExistingAuth = async (): Promise<void> => {
    if (typeof window === 'undefined') return;

    const userData = localStorage.getItem(STORAGE_KEY);
    const authToken = localStorage.getItem('dock_universal_token');

    if (userData && authToken) {
      try {
        const parsedUserData: LoginInfo = JSON.parse(userData);

        // Validate user data structure and required fields
        if (isValidLoginInfo(parsedUserData)) {
          const validation = validateUserData(parsedUserData);

          if (validation.isValid) {
            dispatch(setUser(parsedUserData));
            console.log("Existing auth found, redirecting to dashboard");
            navigate('/tvTerminal');
          } else {
            console.warn("Invalid user data:", validation.errors);
            throw new Error('Invalid user data: ' + validation.errors.join(', '));
          }
        } else {
          throw new Error('Invalid user data format');
        }
      } catch (error) {
        console.log("Invalid existing auth data, clearing storage:", error);
        clearAuthStorage();
        navigate('/apps');
      }
    } else {
      console.log("No existing auth found, redirecting to login");
      navigate('/apps');
    }
  };

  /**
   * Authenticate user with SSO token
   */
  const authenticateWithSSO = async (
    ssoAuthToken: string,
    path: string | null,
    clientCode: string | null,
    version: string | null
  ): Promise<void> => {
    try {
      setIsLoading(true);

      const requestBody: SsoLoginRequest = {
        sso_auth_token: ssoAuthToken,
        application_id: process.env.REACT_APP_TV_APPLICATION_ID || ''
      };

      const response = await flowBackEndService.SsoLogin(requestBody);
      const apiResponse: SsoLoginApiResponse = response.data;

      if (apiResponse.status === AUTH_STATUS.SUCCESS && apiResponse.data) {
        const apiUserData: ApiUserResponse = apiResponse.data;

        // Validate API response structure
        if (!isValidApiUserResponse(apiUserData)) {
          console.error("Invalid API response:", apiUserData);
          throw new Error('Invalid user data received from server');
        }

        // Normalize API response to LoginInfo format
        const loginInfo: LoginInfo = normalizeUserData(
          apiUserData,
          ssoAuthToken,
          apiUserData.deviceId
        );

        // Validate normalized data
        const validation = validateUserData(loginInfo);
        if (!validation.isValid) {
          console.error("Validation errors:", validation.errors);
          throw new Error('Invalid user data: ' + validation.errors.join(', '));
        }

        

        setTimeout(() => {
          sendGTMEvent({event: 'sso-login',mobile: loginInfo.mobile,identifier: loginInfo.identifier});
        }, 1000);

      

        // Store complete user data in localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(loginInfo));

          // Store individual authentication items for backward compatibility
          if (loginInfo.access_token) {
            localStorage.setItem('dock_universal_token', loginInfo.access_token);
          }

          if (loginInfo.user_id) {
            localStorage.setItem('user_id', loginInfo.user_id);
          }

          // Handle client_code using helper function
          const clientCodeValue = getClientCode(apiUserData) || loginInfo.user_id;
          if (clientCodeValue) {
            localStorage.setItem('user_client_code', clientCodeValue);
          }
        }

        // Update Redux store with normalized LoginInfo
        dispatch(setUser(loginInfo));

        // Set session expiry
        const sessionExpiry = moment().add(SESSION_TIME, 'minutes').valueOf();
        dispatch(setSessionTime(sessionExpiry));

        // Set OTP data for other components (using original API data)
        dispatch(setOtpData({
          data: apiUserData,
          user_id: loginInfo.user_id
        }));

        // Set email and mobile for other components
        dispatch(setEmailAndMobile({
          email: loginInfo.email,
          mobile: loginInfo.mobile
        }));

        // Dispatch login event for analytics
        dispatch(loginEvent({
          email: loginInfo.email,
          mobile: loginInfo.mobile,
          identifier: loginInfo.identifier
        }));

        // Navigate to intended path
        const targetPath = path || '/tvTerminal';
        // Small delay to ensure all Redux updates are processed
        setTimeout(() => {
          navigate(targetPath);
        }, 100);

      } else {
        const errorMessage = apiResponse.message || 'Authentication failed';
        console.error("SSO authentication failed:", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('SSO Login Error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Authentication failed';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clear all authentication storage
   */
  const clearAuthStorage = (): void => {
    if (typeof window === 'undefined') return;

    const keysToRemove = [
      STORAGE_KEY,
      'dock_universal_token',
      'user_id',
      'user_client_code',
      'session-time',
      'refresh-data'
    ];

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error(`Failed to remove ${key} from localStorage:`, error);
      }
    });

    console.log("Auth storage cleared");
  };

  /**
   * Handle authentication errors
   */
  const handleAuthError = (message: string): void => {
    console.error("Auth error:", message);
    clearAuthStorage();
    toast.error(message);
    navigate('/apps');
  };

  // Component will navigate away, so no need to render anything after loading
  return null;
};

function sendGTMEvent(eventData: any): void {
  try {
    if (typeof window !== 'undefined' && (window as any).GtmPusher) {
      (window as any).GtmPusher(eventData);
    }
  } catch (error) {
    console.error('Failed to send GTM event:', error);
  }
}

export default AuthStoreWithSSO;